import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { EGender } from '@enums/Gender';
import BigNumber from 'bignumber.js';

import { DisturbiosSodioContext } from './context';

export const DisturbiosSodioProvider = ({ children }: PropsWithChildren) => {
  const [age, setAge] = useState<number>(0);
  const [bloodGlucose, setBloodGlucose] = useState<number>(100);
  const [gender, setGender] = useState<EGender>(EGender.Male);
  const [serumSodium, setSerumSodium] = useState<number>(140);
  const [serumSodiumFixed, setSerumSodiumFixed] = useState<number>(0);
  const [water, setWater] = useState<number>(0);
  const [weight, setWeight] = useState<number>(0);

  const handleAge = useCallback((value: number): void => {
    setAge(value);
  }, []);

  const handleBloodGlucose = useCallback((value: number): void => {
    setBloodGlucose(value);
  }, []);

  const handleGender = useCallback((value: EGender): void => {
    setGender(value);
  }, []);

  const handleSerumSodium = useCallback((value: number): void => {
    setSerumSodium(value);
  }, []);

  const handleWeight = useCallback((value: number): void => {
    setWeight(value);
  }, []);

  const calculateSerumSodiumFixed = useCallback((): void => {
    // FÓRMULA: Se Glicemia <= 400 então: Sódio sérico + (1,6 * ((Glicemia - 100) / 100))
    // FÓRMULA: Se Glicemia > 400 então: Sódio sérico + (2,4 * ((Glicemia - 100) / 100))
    const serumSodiumParsed = new BigNumber(serumSodium);
    const bloodGlucoseParsed = new BigNumber(bloodGlucose);
    if (bloodGlucose <= 400) {
      const step1 = bloodGlucoseParsed.minus(100);
      const step2 = step1.dividedBy(100);
      const step3 = step2.multipliedBy(1.6);
      const result = step3.plus(serumSodiumParsed);
      setSerumSodiumFixed(result.toNumber() || 0);
    } else {
      const step1 = bloodGlucoseParsed.minus(100);
      const step2 = step1.dividedBy(100);
      const step3 = step2.multipliedBy(2.4);
      const result = step3.plus(serumSodiumParsed);
      setSerumSodiumFixed(result.toNumber() || 0);
    }
  }, [bloodGlucose, serumSodium]);

  useEffect(() => {
    calculateSerumSodiumFixed();
  }, [calculateSerumSodiumFixed]);

  const calculateWater = useCallback((): void => {
    // FÓRMULA: Se Masculino e Idade MENOR 60, então Peso x 0,6
    // FÓRMULA: Se Masculino e Idade MAIOR ou IGUAL 60, então Peso x 0,5
    // FÓRMULA: Se Feminino e Idade MENOR 60, então Peso x 0,5
    // FÓRMULA: Se Feminino e Idade MAIOR ou IGUAL 60, então Peso x 0,45
    const weightParsed = new BigNumber(weight);
    if (gender === EGender.Male && age < 60) {
      const result = weightParsed.multipliedBy(0.6);
      setWater(result.toNumber() || 0);
    }
    if (gender === EGender.Male && age >= 60) {
      const result = weightParsed.multipliedBy(0.5);
      setWater(result.toNumber() || 0);
    }
    if (gender === EGender.Female && age < 60) {
      const result = weightParsed.multipliedBy(0.5);
      setWater(result.toNumber() || 0);
    }
    if (gender === EGender.Female && age >= 60) {
      const result = weightParsed.multipliedBy(0.45);
      setWater(result.toNumber() || 0);
    }
  }, [age, gender, weight]);

  useEffect(() => {
    calculateWater();
  }, [calculateWater]);

  return (
    <DisturbiosSodioContext.Provider
      value={{
        age,
        bloodGlucose,
        gender,
        serumSodium,
        serumSodiumFixed,
        setAge: handleAge,
        setBloodGlucose: handleBloodGlucose,
        setGender: handleGender,
        setSerumSodium: handleSerumSodium,
        setWeight: handleWeight,
        water,
        weight,
      }}>
      {children}
    </DisturbiosSodioContext.Provider>
  );
};
